import {useCallback, useEffect, useState, forwardRef, useRef} from 'react';
import {
  NavigationContainer,
  DefaultTheme,
  type LinkingOptions,
  NavigationContainerRef,
} from '@react-navigation/native';

import {SafeAreaProvider} from 'react-native-safe-area-context';
import {TutorialProvider} from '../context/TutorialProvider';
import {AppStackNav} from './app.stack.routes';
import {useTheme} from 'styled-components/native';
import AuthBottomSheet from '../components/shared/AuthBottomSheet/AuthBottomSheet';
import {TutorialModal} from '../components/Interface/TutorialModal';
import {RateInStore} from '../components/shared/RateInStore';
import {GlobalOTPModal} from '../components/shared/GlobalOTPModal';
import {GlobalFinesReminder} from '../components/shared/GlobalFinesReminder';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {useInstallAttribution} from '../utils/installAttribution';
import {setAppsFlyerCustomerUserId} from '../lib/appsflyer';
import {Linking, Platform} from 'react-native';
import {WebStackNav} from './web.stack.routes';
import type {RootStackParamList} from '../dtos/RoutesDTO';
import {useLocalization} from '../context/localization';
import {useAuth} from '../context/auth';
import {addInAppTrigger, bindNotificationOpen} from '../lib/pushMessaging';
import {
  logDeepLinkEvent,
  resolveDeepLink,
  type DeepLinkSource,
  type ResolvedDeepLink,
} from '../utils/deepLinking';

interface RoutesProps {
  onReady?: () => void;
  lat?: number;
  lng?: number;
  token?: string;
  incentiveUserId?: string | null;
  clientId?: string;
  debitoVeicular?: boolean;
  initialDeepLinkUrl?: string | null;
}

import {NAV_STATE_KEY} from '../utils/payments/pendingPayment';

const LINK_SCREENS = {
  Home: {path: 'install*'},
  ReferAndWin: 'ReferAndWin',
  ReferAndWinUsers: 'ReferAndWinUsers',
  ReferAndWinUsersDesactivated: 'ReferAndWinUsersDesactivated',
  ReferAndWinUsersInfo: 'ReferAndWinUsersInfo',
  ListagemPostos: {path: 'Stations/:tipo/:cidade'},
  Localization: {path: 'Stations/:tipo'},
  Checkin: 'Checkin',
  Fipe: 'Fipe',
  FipeDetails: 'FipeDetails',
  LoadingFipe: 'LoadingFipe',
  Coupons: 'Coupons',
  Shopping: 'Shopping',
  LoadingClube: 'LoadingClube',
  Vehicles: 'Vehicles',
  Emprestimo: {path: 'Emprestimo'},
  Reembolsos: {path: 'Reembolsos'},
  SolicitaReembolso: {path: 'SolicitaReembolso'},
  PedidosReembolso: {path: 'PedidosReembolso'},
  BaratinhasAjuda: {path: 'BaratinhasAjuda'},
  Roulette: {path: 'Roulette'},
  Achievements: {path: 'Achievements'},
  SubRegions: 'SubRegions',
  Stations: 'Stations',
  Station: 'Station',
  Products: 'Products',
  Product: 'Product',
  Fuel: 'Fuel',
  CardsWallet: 'CardsWallet',
  Payment: 'Payment',
  Pix: 'Pix',
  VehicleDebits: {path: 'VehicleDebits'},
  Emprestimos: 'Emprestimos',
  Simular: 'Simular',
};

export const Routes = forwardRef<NavigationContainerRef<any>, RoutesProps>(
  (props, ref) => {
    const hasInitialDeepLinkUrlProp = Object.prototype.hasOwnProperty.call(
      props,
      'initialDeepLinkUrl',
    );
    const {
      onReady,
      lat,
      lng,
      token,
      incentiveUserId,
      clientId,
      debitoVeicular,
      initialDeepLinkUrl,
    } = props;

    const theme = useTheme();
    const {setPosition} = useLocalization();
    const [showModalFeedback, setShowModalFeedback] = useState(false);
    const routeNameRef = useRef<string | undefined>();
    const [isNavReady, setIsNavReady] = useState(false);
    const [isContainerReady, setIsContainerReady] = useState(false);
    const initialUrlCheckedRef = useRef(false);
    const pendingAuthenticatedLinkRef = useRef<ResolvedDeepLink | null>(null);

    useEffect(() => {
      if (Platform.OS === 'web') {
        setIsNavReady(true);
        return;
      }

      setIsNavReady(true);
    }, []);

    const {
      user,
      fetchUserData,
      setClientId,
      isLogged,
      isAuthReady,
      setShowSign,
      setSignDismissable,
    } = useAuth();

    useEffect(() => {
      if (Platform.OS === 'web' && clientId) {
        setClientId(clientId);
      }
    }, [clientId]);

    useEffect(() => {
      async function externalToken() {
        if (token && Platform.OS === 'web') {
          await fetchUserData(token, incentiveUserId, clientId);
        }
      }

      externalToken();
    }, [token]);

    useEffect(() => {
      if (Platform.OS === 'web' && lat && lng) {
        setPosition({latitude: lat, longitude: lng});
      }
    }, [lat, lng]);

    // Hook para capturar atribuição de instalação
    useInstallAttribution();

    // customerUserId: liga o usuário ao AppsFlyer, habilita LTV por campanha.
    useEffect(() => {
      if (user?.user_id && user.user_id !== 'convidado') {
        setAppsFlyerCustomerUserId(user.user_id);
      }
    }, [user?.user_id]);

    /// Verificar se o tema está disponível antes de usar
    const navTheme = {
      ...DefaultTheme,
      colors: {
        ...DefaultTheme.colors,
        background: theme?.colors?.background || '#ffffff',
      },
    };

    const linking: LinkingOptions<RootStackParamList> = {
      prefixes: [
        'barataoapp://',
        'https://usebaratao.com.br',
        'https://baratao-phi.vercel.app',
        Platform.OS === 'web' && typeof window !== 'undefined'
          ? window.location.origin
          : '',
      ],
      config: {
        screens: LINK_SCREENS,
      },
      async getInitialURL() {
        if (Platform.OS === 'web') {
          if (typeof window !== 'undefined') {
            return window.location.href; // garante a URL completa com query
          }
        }

        try {
          const url = await Linking.getInitialURL();
          return url ?? undefined;
        } catch {
          return undefined;
        }
      },
      subscribe(listener) {
        const onReceiveURL = ({url}: {url: string}) => {
          console.log('🔗 URL changed:', url);
          listener(url);
        };

        // Assinar eventos no web / mobile
        const subscription = Linking.addEventListener('url', onReceiveURL);
        return () => subscription.remove();
      },
    };

    const getNavigation = useCallback(() => {
      if (!ref || typeof ref === 'function') {
        return null;
      }

      return ref.current;
    }, [ref]);

    const isLoggedRef = useRef(isLogged);
    const lastHandledDeepLinkRef = useRef<{
      url: string;
      at: number;
    } | null>(null);

    useEffect(() => {
      isLoggedRef.current = isLogged;
    }, [isLogged]);

    const navigateResolvedDeepLink = useCallback(
      (resolved: ResolvedDeepLink) => {
        const navigation = getNavigation();

        if (!navigation) {
          if (__DEV__) {
            console.log('[DeepLink] Navigation ref unavailable');
          }
          return false;
        }

        try {
          if (__DEV__) {
            console.log('[DeepLink] Navigating:', {
              screen: resolved.screen,
              routeName: resolved.routeName,
              navigationKind: resolved.navigationKind,
              params: resolved.params,
            });
          }

          const navigate = navigation.navigate as (...args: any[]) => void;

          if (resolved.navigationKind === 'tab') {
            navigate('Home', {
              screen: resolved.routeName,
              params: resolved.params,
            });
            return true;
          }

          if (resolved.navigationKind === 'home') {
            navigate('Home');
            return true;
          }

          navigate(resolved.routeName, resolved.params ?? {});
          return true;
        } catch (error) {
          console.error('[DeepLink] Error navigating:', error);

          try {
            navigation.navigate('Home' as never);
          } catch {}

          return false;
        }
      },
      [getNavigation],
    );

    const handleDeepLink = useCallback(
      async (url: string, source: DeepLinkSource) => {
        const now = Date.now();
        const lastHandled = lastHandledDeepLinkRef.current;

        if (lastHandled?.url === url && now - lastHandled.at < 1000) {
          return;
        }

        lastHandledDeepLinkRef.current = {url, at: now};

        if (__DEV__) {
          console.log('[DeepLink] Handling URL:', {source, url});
        }

        // Sites externos (http/https fora dos domínios do app) abrem no navegador
        const isExternal =
          /^https?:\/\//i.test(url) &&
          !['usebaratao.com.br', 'baratao-phi.vercel.app'].some(host =>
            url.includes(host),
          );

        if (isExternal) {
          Linking.openURL(url).catch(error => {
            console.error('[DeepLink] Error opening external URL:', error);
          });
          return;
        }

        const resolved = resolveDeepLink(url, source);

        if (__DEV__) {
          console.log('[DeepLink] Resolved URL:', {
            source: resolved.source,
            isValid: resolved.isValid,
            screen: resolved.screen,
            routeName: resolved.routeName,
            navigationKind: resolved.navigationKind,
            requiresAuth: resolved.requiresAuth,
            reason: resolved.reason,
          });
        }

        await logDeepLinkEvent('deep_link_open', resolved);

        if (!resolved.isValid) {
          await logDeepLinkEvent('deep_link_invalid', resolved);
          navigateResolvedDeepLink(resolved);
          return;
        }

        if (resolved.requiresAuth && !isLoggedRef.current) {
          pendingAuthenticatedLinkRef.current = resolved;
          setSignDismissable(true);
          setShowSign(true);
          await logDeepLinkEvent('deep_link_requires_auth', resolved);
          return;
        }

        if (navigateResolvedDeepLink(resolved)) {
          await logDeepLinkEvent('deep_link_resolved', resolved);
        }
      },
      [navigateResolvedDeepLink, setShowSign, setSignDismissable],
    );

    useEffect(() => {
      if (
        Platform.OS === 'web' ||
        !isNavReady ||
        !isContainerReady ||
        !isAuthReady
      ) {
        return;
      }

      if (!initialUrlCheckedRef.current) {
        if (hasInitialDeepLinkUrlProp && initialDeepLinkUrl === undefined) {
          return;
        }

        initialUrlCheckedRef.current = true;
        Promise.resolve(
          hasInitialDeepLinkUrlProp ? initialDeepLinkUrl : Linking.getInitialURL(),
        )
          .then(url => {
            if (__DEV__) {
              console.log('[DeepLink] Initial URL received by Routes:', url);
            }

            if (url) {
              handleDeepLink(url, 'initial');
            }
          })
          .catch(error => {
            console.error('[DeepLink] Error reading initial URL:', error);
          });
      }

      const urlSubscription = Linking.addEventListener('url', event => {
        handleDeepLink(event.url, 'event');
      });

      const unsubscribePush = bindNotificationOpen(url => {
        handleDeepLink(url, 'push');
      });

      return () => {
        urlSubscription.remove();
        try {
          unsubscribePush?.();
        } catch {}
      };
    }, [
      handleDeepLink,
      hasInitialDeepLinkUrlProp,
      initialDeepLinkUrl,
      isAuthReady,
      isContainerReady,
      isNavReady,
    ]);

    useEffect(() => {
      if (
        Platform.OS === 'web' ||
        !isNavReady ||
        !isContainerReady ||
        !isAuthReady ||
        !isLogged
      ) {
        return;
      }

      const pending = pendingAuthenticatedLinkRef.current;
      if (!pending) {
        return;
      }

      pendingAuthenticatedLinkRef.current = null;

      if (navigateResolvedDeepLink(pending)) {
        logDeepLinkEvent('deep_link_resolved', pending);
      }
    }, [
      isAuthReady,
      isContainerReady,
      isLogged,
      isNavReady,
      navigateResolvedDeepLink,
    ]);

    const checkFirstInteractionAndShowModal = async () => {
      try {
        const firstInteraction = await AsyncStorage.getItem('firstInteraction');

        if (firstInteraction === 'true') {
          await AsyncStorage.setItem('firstInteraction', 'completed');
          checkSecondInteractionAndShowModal();
        } else if (firstInteraction === 'completed') {
          checkSecondInteractionAndShowModal();
        } else {
          await AsyncStorage.setItem('firstInteraction', 'true');
        }
      } catch (error) {
        console.error('Erro ao verificar a primeira interação', error);
      }
    };

    const checkSecondInteractionAndShowModal = async () => {
      try {
        const secondInteraction = await AsyncStorage.getItem(
          'secondInteraction',
        );

        if (secondInteraction === 'true') {
          setShowModalFeedback(true);
          await AsyncStorage.setItem('secondInteraction', 'completed');
          await AsyncStorage.setItem('lastSendReview', new Date().toString());
        } else if (secondInteraction === 'completed') {
          showModalInOneWeek();
        } else {
          await AsyncStorage.setItem('secondInteraction', 'true');
        }
      } catch (error) {
        console.error('Erro ao verificar a primeira interação', error);
      }
    };

    const showModalInOneWeek = async () => {
      const lastSendReview = await AsyncStorage.getItem('lastSendReview');

      if (lastSendReview) {
        const currentTime = new Date();
        const lastInteraction = new Date(lastSendReview);

        const lastInteractionTime = Math.abs(
          currentTime.getTime() - lastInteraction.getTime(),
        );

        const millisecondsIn1week = 7 * 24 * 60 * 60 * 1000;

        if (lastInteractionTime >= millisecondsIn1week) {
          setShowModalFeedback(true);
          await AsyncStorage.setItem('lastSendReview', new Date().toString());
        }
      }
    };

    useEffect(() => {
      if (Platform.OS !== 'web') {
        checkFirstInteractionAndShowModal();
      }
    }, []);

    if (!theme || !theme.colors || !isNavReady) {
      return null;
    }

    return (
      <SafeAreaProvider>
        <NavigationContainer
          theme={navTheme}
          ref={ref}
          linking={Platform.OS === 'web' ? linking : undefined}
          onReady={() => {
            setIsContainerReady(true);
            if (Platform.OS !== 'web') {
              addInAppTrigger('on', 'app_open');
            }
            const navigation = getNavigation();
            if (navigation) {
              routeNameRef.current = navigation.getCurrentRoute()?.name;
              if (routeNameRef.current && Platform.OS !== 'web') {
                addInAppTrigger('screen', routeNameRef.current);
              }
            }
            if (onReady) {
              onReady();
            }
          }}
          onStateChange={state => {
            if (Platform.OS !== 'web' && state) {
              AsyncStorage.setItem(NAV_STATE_KEY, JSON.stringify(state));
            }
            const navigation = getNavigation();
            if (navigation) {
              const currentRouteName = navigation.getCurrentRoute()?.name;
              if (
                currentRouteName &&
                currentRouteName !== routeNameRef.current
              ) {
                routeNameRef.current = currentRouteName;
                if (Platform.OS !== 'web') {
                  addInAppTrigger('screen', currentRouteName);
                }
              }
            }
          }}>
          {Platform.OS !== 'web' ? (
            <TutorialProvider>
              <AppStackNav />
              <AuthBottomSheet />
              <TutorialModal />
              <RateInStore
                isVisible={showModalFeedback}
                onDismiss={() => setShowModalFeedback(false)}
              />
              <GlobalOTPModal />
              <GlobalFinesReminder />
            </TutorialProvider>
          ) : (
            <>
              <WebStackNav debitoVeicular={debitoVeicular} />
              <AuthBottomSheet />
              <GlobalOTPModal />
              <GlobalFinesReminder />
            </>
          )}
        </NavigationContainer>
      </SafeAreaProvider>
    );
  },
);
